import React, { useState, useRef, useEffect, useMemo } from 'react'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import useSWR from 'swr'
import { BASE_URL, fetcher, fetchAll } from '../../utils/api'
import Spinner from '../Spinner'
import { FiUpload, FiX, FiMic, FiSquare, FiPlay, FiPause } from 'react-icons/fi'
import useAuth from '../../context/userContext'
import { ErrorNotification, SuccessNotification } from '../../utils'
import Select from 'react-select'
import api from '../../utils/api'
import ScheduleRepeatFields from '../scheduling/ScheduleRepeatFields'
import {
  CAMPAIGN_REPEAT_OPTIONS,
  buildScheduleApiFields,
  validateScheduleConfig,
  parseScheduleFromApi,
  appendScheduleFieldsToFormData,
} from '../../utils/scheduleRepeat'
import {
  audioBufferToWav,
  convertWavToMp3,
  prepareCampaignAudioUpload,
} from '../../utils/audioConversion'

const MESSAGE_TYPES = [
  { value: 'sms', label: 'SMS' },
  { value: 'voice', label: 'Voice' },
]
const REPEAT_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'one_time', label: 'Once' },
  { value: 'weekly', label: 'Day Of The Week' },
  { value: 'monthly', label: 'Monthly' },
]

const NewCampaignModal = ({
  onClose,
  mutate,
  openSMSModal,
}) => {
  // Form state
  const { user } = useAuth()

  // Fetch SMS sender IDs
  const { data: smsSenderIdsData, isLoading: loadingSenderIds, mutate: mutateSenderIds } = useSWR(`sms-sender-ids/`, fetcher)

  // Process SMS sender IDs
  const smsSenderIds = Array.isArray(smsSenderIdsData) ? smsSenderIdsData : []
  const approvedSenderIds = useMemo(() =>
    smsSenderIds.filter(id => id.status === 'approved'),
    [smsSenderIds]
  )
  const nonApprovedSenderIds = useMemo(() =>
    smsSenderIds.filter(id => id.status !== 'approved'),
    [smsSenderIds]
  )

  // Get the default sender ID (first approved and enabled, or first approved, or first non-approved)
  const defaultSenderId = useMemo(() =>
    approvedSenderIds[0]?.effective_sender_id || "Solidaridad",
    [approvedSenderIds]
  )

  const [smsNickname, setSmsNickname] = useState(defaultSenderId)
  const [selectedSenderId, setSelectedSenderId] = useState(null)
  const [messageTitle, setMessageTitle] = useState("")
  const [messageType, setMessageType] = useState("")
  const [audioFile, setAudioFile] = useState(null)
  const [recipients, setRecipients] = useState([])
  const [messageBody, setMessageBody] = useState("")
  const [description, setDescription] = useState("")
  const [schedule, setSchedule] = useState(false)
  const [repeat, setRepeat] = useState('one_time')
  const [date, setDate] = useState('')
  const [day, setDay] = useState("Monday")
  const [selectedMonths, setSelectedMonths] = useState([])
  const [dayOfMonth, setDayOfMonth] = useState('')
  const [time, setTime] = useState(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }))
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  // Audio recording states
  const [isRecording, setIsRecording] = useState(false)
  const [recordedAudio, setRecordedAudio] = useState(null)
  const [audioBlob, setAudioBlob] = useState(null)
  const [recordingTime, setRecordingTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [audioMode, setAudioMode] = useState('upload')
  const [audioProcessing, setAudioProcessing] = useState(false)

  // Refs for audio recording
  const mediaRecorderRef = useRef(null)
  const audioRef = useRef(null)
  const audioFileInputRef = useRef(null)
  const recordingIntervalRef = useRef(null)

  // Fetch groups with SWR
  const { data: groupsData, isLoading: loadingGroups } = useSWR(`groups/`, fetchAll)
  const groupOptions = Array.isArray(groupsData) ? groupsData : []
  const groupSelectOptions = groupOptions.map(g => ({ value: g.id, label: `${g.group_name} - (${g.total_members} members)` }))

  // Filter to only show enabled approved sender IDs
  const availableSenderIds = approvedSenderIds.filter(id => id.is_enabled)

  // Update SMS nickname and selection when sender IDs load
  useEffect(() => {
    if (availableSenderIds.length > 0 && !selectedSenderId) {
      // Auto-select first enabled approved sender ID, or first approved
      const autoSelectId = availableSenderIds[0]

      if (autoSelectId) {
        setSelectedSenderId(autoSelectId)
        setSmsNickname(autoSelectId.effective_sender_id)
      }
    } else if (smsSenderIds.length === 0 && defaultSenderId && !smsNickname) {
      setSmsNickname(defaultSenderId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smsSenderIds.length])

  // Handle sender ID selection
  const handleSelectSenderId = (senderId) => {
    setSelectedSenderId(senderId)
    setSmsNickname(senderId.effective_sender_id)
  }

  // File upload handler
  const handleAudioUpload = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.target.files?.[0]) return

    const selectedFile = e.target.files[0]
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase()
    const allowedExtensions = ['mp3', 'wav', 'm4a', 'mp4']

    if (!allowedExtensions.includes(fileExtension)) {
      ErrorNotification('Invalid file type. Only .mp3, .m4a, .mp4 and .wav files are supported.')
      e.target.value = null
      return
    }

    setAudioProcessing(true)
    try {
      const preparedFile = await prepareCampaignAudioUpload(selectedFile)
      setAudioFile(preparedFile)
      setRecordedAudio(null)
      setAudioBlob(null)
      setAudioMode('upload')

      if (fileExtension === 'm4a' || fileExtension === 'mp4') {
        SuccessNotification('Audio converted to MP3 successfully')
      }
    } catch (error) {
      console.error('Error preparing audio upload:', error)
      ErrorNotification('Failed to process audio file. Please try an .mp3 file.')
      e.target.value = null
    } finally {
      setAudioProcessing(false)
    }
  }

  // Remove audio
  const removeAudio = () => {
    setAudioFile(null)
    setRecordedAudio(null)
    setAudioBlob(null)
    setRecordingTime(0)
    if (audioFileInputRef.current) audioFileInputRef.current.value = ''
  }

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Try to use MP3 mimeType if supported, otherwise fallback to default
      const options = {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/webm;codecs=opus'
      }
      mediaRecorderRef.current = new MediaRecorder(stream, options)
      const chunks = []

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data)
        }
      }

      mediaRecorderRef.current.onstop = async () => {
        try {
          const audioBlob = new Blob(chunks, { type: chunks[0]?.type || 'audio/webm' })

          // Convert to WAV first, then to MP3
          // We need to decode the audio and re-encode as MP3
          const audioContext = new (window.AudioContext || window.webkitAudioContext)()
          const arrayBuffer = await audioBlob.arrayBuffer()
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

          // Convert AudioBuffer to WAV
          const wav = audioBufferToWav(audioBuffer)
          const wavBlob = new Blob([wav], { type: 'audio/wav' })

          // Convert WAV to MP3
          const mp3Blob = await convertWavToMp3(wavBlob)

          setAudioBlob(mp3Blob)
          const audioUrl = URL.createObjectURL(mp3Blob)
          setRecordedAudio(audioUrl)
          setAudioFile(new File([mp3Blob], 'recording.mp3', { type: 'audio/mp3' }))
          setAudioMode('record')
          stream.getTracks().forEach(track => track.stop())
        } catch (error) {
          console.error('Error converting audio to MP3:', error)
          ErrorNotification('Error processing audio. Please try again.')
          stream.getTracks().forEach(track => track.stop())
        }
      }

      mediaRecorderRef.current.start()
      setIsRecording(true)
      setRecordingTime(0)

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1)
      }, 1000)
    } catch (error) {
      ErrorNotification('Error accessing microphone. Please check permissions.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current)
      }
    }
  }

  const playRecordedAudio = () => {
    if (recordedAudio && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
        setIsPlaying(false)
      } else {
        audioRef.current.play()
        setIsPlaying(true)
      }
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current)
      }
      if (recordedAudio) {
        URL.revokeObjectURL(recordedAudio)
      }
    }
  }, [recordedAudio])

  // --- Submit Handler ---
  const handleFormSubmit = async (e) => {
    e.preventDefault()
    // Validation
    const newErrors = {}
    if (!smsNickname) newErrors.smsNickname = 'SMS Nickname is required.'
    if (!messageTitle) newErrors.messageTitle = 'Message Title is required.'
    if (!messageType) newErrors.messageType = 'Message Type is required.'
    if (!recipients || recipients.length === 0) newErrors.recipients = 'Please select at least one group.'
    if (messageType === 'sms' && !messageBody) newErrors.messageBody = 'Message is required.'
    if (messageType === 'voice' && !audioFile) newErrors.audioFile = 'Audio file is required.'
    if (schedule) {
      if (!repeat) newErrors.repeat = 'Repeat option is required.'
      Object.assign(newErrors, validateScheduleConfig({
        repeat,
        date,
        time,
        day,
        months: selectedMonths,
        dayOfMonth,
      }))
    }
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      // Scroll to first error
      const firstErrorKey = Object.keys(newErrors)[0]
      const el =
        document.getElementById(firstErrorKey) ||
        document.querySelector(`[name="${firstErrorKey}"]`) ||
        (firstErrorKey === 'scheduleFuture' ? document.getElementById('campaign-schedule-error') : null)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    setSubmitting(true)
    try {
      const token = localStorage.getItem('token')
      const formData = new FormData()
      formData.append('sms_nickname', smsNickname || defaultSenderId)
      formData.append('sms_sender_id', smsNickname || defaultSenderId)
      formData.append('message_title', messageTitle)
      formData.append('message_type', messageType === "voice" ? "audio" : messageType)
      
      formData.append('description', description)
      formData.append('message_body', messageBody ? messageBody : description)

      if (audioFile && messageType === 'voice') {
        formData.append('audio_file', audioFile)
      }
      if (recipients && recipients.length > 0) {
        recipients.forEach(r => formData.append('recipients', r.value))
      }
      if (schedule) {
        appendScheduleFieldsToFormData(formData, buildScheduleApiFields({
          repeat,
          date,
          time,
          day,
          months: selectedMonths,
          dayOfMonth,
        }))
      }
      const res = await fetch(`${BASE_URL}campaigns/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      })
      if (!res.ok) throw new Error('Failed to create campaign')
      mutate()
      SuccessNotification('Campaign created successfully!')
      onClose()
    } catch (err) {
      ErrorNotification(err?.message || 'Failed to create campaign')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white overflow-hidden dark:bg-dark rounded-lg shadow-lg w-full md:max-w-[600px] max-w-[90vw] relative max-h-[90vh] flex flex-col">
        <input
          ref={audioFileInputRef}
          type="file"
          accept=".mp3,.m4a,.mp4,.wav,audio/mpeg,audio/wav,audio/mp4,audio/x-m4a,video/mp4"
          className="hidden"
          onChange={handleAudioUpload}
          disabled={audioProcessing}
          tabIndex={-1}
          aria-hidden="true"
        />
        <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
          <h2 className='text-xl font-bold dark:text-white'>
            Create New Campaign
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2 gap-2">
            <span className="text-yellowcolor text-lg font-semibold">Campaign Details</span>
            {/* <button
              type='button'
              onClick={openSMSModal}
              className="border border-gray-400 dark:border-border-dark dark:text-white rounded-md px-4 py-1 text-sm font-medium">Request Custom ID</button> */}
          </div>
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs rounded-md px-4 py-2 mb-4 flex items-center">
            If you have not requested for a custom sms nickname yet, your campaigns will be sent with the default "HARVSTALERT" SMS nickname
          </div>


          {/* Message Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 dark:text-white">Message Type</label>
            <select
              className={`w-full border ${errors.messageType ? 'border-red-500' : 'border-gray-200'} rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400 dark:text-white dark:bg-secondaryDark dark:border-border-dark`}
              value={messageType}
              onChange={e => setMessageType(e.target.value)}
              name="messageType"
            >
              <option value="">Select message type</option>
              {MESSAGE_TYPES.map(mt => (
                <option key={mt.value} value={mt.value}>{mt.label}</option>
              ))}
            </select>
            {errors.messageType && <div className="text-xs text-red-500 mt-1">{errors.messageType}</div>}
          </div>


          {/* SMS Nickname */}
          {messageType !== 'voice' && (
            <div className="mb-4">
            <label className="block text-sm font-medium mb-1 dark:text-white">SMS Nickname</label>

              {loadingSenderIds ? (
                <div className="flex items-center gap-2">
                  <Spinner />
                  <span className="text-sm text-gray-500 dark:text-gray-400">Loading SMS Nicknames...</span>
                </div>
              ) : (
                  <div className="space-y-2">
                    {/* Available Sender IDs (Enabled Approved) */}
                    {availableSenderIds.length > 0 && (
                      <div className="space-y-1.5">
                        {availableSenderIds.map((senderId) => (
                        <div
                          key={senderId.id}
                            className={`p-2 rounded border transition-colors ${selectedSenderId?.id === senderId.id
                            ? 'border-yellowcolor bg-yellow-50 dark:bg-yellow-900/20'
                              : 'border-gray-200 dark:border-border-dark bg-white dark:bg-secondaryDark hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-center gap-2.5">
                              <input
                                type="radio"
                                name="smsSenderId"
                                id={`sender-${senderId.id}`}
                                checked={selectedSenderId?.id === senderId.id}
                                onChange={() => handleSelectSenderId(senderId)}
                                className="text-yellowcolor focus:ring-yellowcolor w-4 h-4"
                              />
                              <label
                                htmlFor={`sender-${senderId.id}`}
                                className="flex-1 cursor-pointer flex items-center justify-between gap-2"
                              >
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-sm dark:text-white">
                                    {senderId.effective_sender_id}
                                  </span>
                                  <span className="px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded text-xs font-medium">
                                    Approved
                                  </span>
                                  {senderId.is_enabled && (
                                    <span className="px-1.5 py-0.5 bg-yellowcolor/20 text-yellow-800 dark:text-yellow-400 rounded text-xs font-medium">
                                      Enabled
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                                  {senderId.sender_id}
                                </span>
                              </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                    {/* Show message if no enabled sender IDs */}
                    {availableSenderIds.length === 0 && approvedSenderIds.length > 0 && (
                      <div className="p-2.5 rounded border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
                        <p className="text-xs text-yellow-800 dark:text-yellow-300">
                          No enabled SMS Nicknames available. We will use the default SMS Nickname: <span className="font-semibold">{defaultSenderId}</span>. Please enable a SMS Nickname in Settings to use it for campaigns.
                        </p>
                      </div>
                    )}

                  {/* No Sender IDs Available */}
                  {smsSenderIds.length === 0 && !loadingSenderIds && (
                      <div className="p-2.5 rounded border border-gray-200 dark:border-border-dark bg-white dark:bg-secondaryDark">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                        No SMS Nicknames found. Using default: {defaultSenderId}
                      </div>
                    </div>
                  )}

                  {/* Fallback if no selection */}
                  {smsSenderIds.length === 0 && (
                    <input
                      type="hidden"
                      name="smsNickname"
                      value={smsNickname}
                    />
                  )}
                </div>
              )}

            {errors.smsNickname && <div className="text-xs text-red-500 mt-1">{errors.smsNickname}</div>}
            </div>
          )}

          {/* Message Title */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 dark:text-white">Message Title</label>
            <input
              className={`w-full border ${errors.messageTitle ? 'border-red-500' : 'border-gray-200'} rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor dark:bg-secondaryDark focus:border-transparent placeholder-gray-400 dark:text-white dark:border-border-dark`}
              placeholder="Enter message title"
              value={messageTitle}
              onChange={e => setMessageTitle(e.target.value)}
              name="messageTitle"
            />
            {errors.messageTitle && <div className="text-xs text-red-500 mt-1">{errors.messageTitle}</div>}
          </div>

          {/* Audio Input (if voice) */}
          {messageType === 'voice' && (
            <div className="mb-4" id="audioFile">
              <label className="block text-sm font-medium mb-1 dark:text-white">Audio Input</label>

              {/* Audio Mode Toggle */}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setAudioMode('upload')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${audioMode === 'upload'
                    ? 'bg-yellowcolor text-black'
                    : 'bg-gray-200 dark:bg-secondaryDark text-gray-700 dark:text-white'
                    }`}
                >
                  Upload File
                </button>
                <button
                  type="button"
                  onClick={() => setAudioMode('record')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${audioMode === 'record'
                    ? 'bg-yellowcolor text-black'
                    : 'bg-gray-200 dark:bg-secondaryDark text-gray-700 dark:text-white'
                    }`}
                >
                  Record Audio
                </button>
              </div>

              {/* Upload Mode */}
              {audioMode === 'upload' && (
                <div className="flex flex-col md:flex-row gap-2 w-full">
                  <div className="flex-1 flex flex-row gap-2 items-center">
                    {audioFile ? (
                      <div className="flex-1 flex flex-row items-center bg-gray-200 rounded-md px-4 py-2 w-full dark:text-white dark:bg-secondaryDark dark:border-border-dark">
                        <span className="flex-1 truncate">{audioFile.name}</span>
                        <button type="button" onClick={removeAudio} className="ml-2 text-gray-500 hover:text-red-500"><FiX /></button>
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col justify-center">
                        <p className="text-sm text-gray-500 dark:text-gray-400">No audio file selected</p>
                        <p className="text-[10px] text-blue-600 mt-1 font-semibold uppercase">
                          Supported formats: .mp3, .m4a, .mp4, .wav (.m4a/.mp4 converted to .mp3)
                        </p>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="border border-yellowcolor text-yellowcolor rounded-md px-4 py-2 font-medium ml-2 disabled:opacity-50"
                    onClick={() => audioFileInputRef.current?.click()}
                    disabled={audioProcessing}
                  >
                    <FiUpload className="inline mr-1" />
                    {audioProcessing ? 'Converting...' : 'Upload'}
                  </button>
                </div>
              )}

              {/* Recording Mode */}
              {audioMode === 'record' && (
                <div className="space-y-3">
                  {/* Recording Controls */}
                  <div className="flex items-center gap-3">
                    {!isRecording ? (
                      <button
                        type="button"
                        onClick={startRecording}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md font-medium transition-colors"
                      >
                        <FiMic className="w-4 h-4" />
                        Start Recording
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={stopRecording}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium transition-colors"
                      >
                        <FiSquare className="w-4 h-4" />
                        Stop Recording
                      </button>
                    )}

                    {recordingTime > 0 && (
                      <span className="text-sm font-medium dark:text-white">
                        {formatTime(recordingTime)}
                      </span>
                    )}
                  </div>

                  {/* Recorded Audio Display */}
                  {recordedAudio && (
                    <div className="flex items-center gap-3 p-3 bg-gray-100 dark:bg-secondaryDark rounded-md">
                      <button
                        type="button"
                        onClick={playRecordedAudio}
                        className="flex items-center gap-2 px-3 py-1 bg-yellowcolor hover:bg-yellow-600 text-black rounded-md text-sm font-medium transition-colors"
                      >
                        {isPlaying ? <FiPause className="w-4 h-4" /> : <FiPlay className="w-4 h-4" />}
                        {isPlaying ? 'Pause' : 'Play'}
                      </button>

                      <span className="text-sm dark:text-white flex-1">
                        Recording ({formatTime(recordingTime)})
                      </span>

                      <button
                        type="button"
                        onClick={removeAudio}
                        className="text-gray-500 hover:text-red-500"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Hidden audio element for playback */}
                  {recordedAudio && (
                    <audio
                      ref={audioRef}
                      src={recordedAudio}
                      onEnded={() => setIsPlaying(false)}
                      onPause={() => setIsPlaying(false)}
                      onPlay={() => setIsPlaying(true)}
                    />
                  )}
                </div>
              )}

              {errors.audioFile && <div className="text-xs text-red-500 mt-1">{errors.audioFile}</div>}
            </div>
          )}

          {/* Send Message To */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 dark:text-white">Send Message To</label>
            <Select
              isMulti
              options={groupSelectOptions}
              value={recipients}
              onChange={setRecipients}
              placeholder="Select groups..."
              className="text-sm"
              classNamePrefix="select"
              isDisabled={loadingGroups}
              name="recipients"
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#fbbf24' : '#e5e7eb',
                  boxShadow: state.isFocused ? '0 0 0 1px #fbbf24' : 'none',
                  '&:hover': {
                    borderColor: '#fbbf24'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected ? '#fbbf24' : state.isFocused ? '#fef3c7' : 'white',
                  color: state.isSelected ? 'white' : 'black',
                  '&:hover': {
                    backgroundColor: state.isSelected ? '#fbbf24' : '#fef3c7'
                  }
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: '#fbbf24',
                  color: 'white'
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: 'white'
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#d97706',
                    color: 'white'
                  }
                })
              }}
            />
            {errors.recipients && <div className="text-xs text-red-500 mt-1">{errors.recipients}</div>}
          </div>

          {/* Message To Be Sent (for SMS) */}
          {/* {messageType === 'sms' && ( */}
          {messageType !== 'voice' && <div className="mb-4">
            <label className="block text-sm font-medium mb-1 dark:text-white">Message To Be Sent</label>
            <textarea
              className={`w-full border ${errors.messageBody ? 'border-red-500' : 'border-gray-200'} rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400 dark:text-white dark:bg-secondaryDark dark:border-border-dark`}
              placeholder="Type message here"
              value={messageBody}
              onChange={e => setMessageBody(e.target.value)}
              rows={3}
              name="messageBody"
            />
            {errors.messageBody && <div className="text-xs text-red-500 mt-1">{errors.messageBody}</div>}
          </div>}
          {/* )} */}

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 dark:text-white">Description</label>
            <textarea
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              placeholder="Enter campaign description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {/* Schedule Toggle */}
          <div className="mb-4 flex items-center gap-2">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={schedule}
                onChange={e => setSchedule(e.target.checked)}
                className="sr-only peer"
                name="schedule"
              />
              <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${schedule ? 'bg-yellowcolor' : 'bg-gray-200'} peer-focus:ring-2 peer-focus:ring-yellowcolor relative`}>
                <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${schedule ? 'translate-x-5' : ''}`}></div>
              </div>
            </label>
            <span className="text-sm font-medium dark:text-white">Schedule This Campaign</span>
          </div>

          {/* Scheduling Options */}
          {schedule && (
            <div className="mb-4 space-y-3" id="campaign-schedule-error">
              {errors.scheduleFuture && (
                <div className="text-sm text-red-500 rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-3 py-2">
                  {errors.scheduleFuture}
                </div>
              )}
              <ScheduleRepeatFields
                repeat={repeat}
                onRepeatChange={setRepeat}
                repeatOptions={CAMPAIGN_REPEAT_OPTIONS}
                date={date}
                onDateChange={setDate}
                day={day}
                onDayChange={setDay}
                months={selectedMonths}
                onMonthsChange={setSelectedMonths}
                dayOfMonth={dayOfMonth}
                onDayOfMonthChange={setDayOfMonth}
                time={time}
                onTimeChange={setTime}
                errors={errors}
              />
            </div>
          )}
        </div>

        <div className='border-t p-4 z-50 border-gray-200 flex flex-row items-end justify-end w-full bg-white dark:bg-dark flex-shrink-0 dark:border-border-dark'>
          <div className="flex flex-row items-end justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'>
              Close
            </button>

            <button
              type="button"
              onClick={handleFormSubmit}
              className={`px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={submitting}
            >
              {submitting ? "Please wait..." : "Send Message"}
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}

export default NewCampaignModal